import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { articles, categories, trendingHeadlines } from '@/mocks/archive';
import {
  byCategory,
  CategoryBadge,
  getArticle,
  getCategory,
  MetaLine,
  PostCardSpot,
  PostCardVertical,
  RankRow,
  SectionHead,
} from '@/components/feature/PostCard';
import { formatViews, tone } from '@/utils/chigua';

function usePageMeta(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

function Ticker() {
  const list = [...trendingHeadlines, ...trendingHeadlines];
  return (
    <div className="relative flex items-center gap-3 overflow-hidden bg-foreground-950 py-2.5">
      <span className="z-10 ml-3 inline-flex shrink-0 items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
        <i className="ri-flashlight-fill" aria-hidden="true" />
        吃瓜快讯
      </span>
      <div className="relative flex-1 overflow-hidden">
        <div className="marquee-track items-center gap-8">
          {list.map((h, i) => (
            <span key={i} className="inline-flex items-center gap-8 text-sm text-background-100">
              <span>{h}</span>
              <i className="ri-close-line text-primary-400" aria-hidden="true" />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Hero() {
  const featured = getArticle('tafang-mofu')!;
  const side = [getArticle('lianqing-tongchuang')!, getArticle('juzu-xuancai')!];
  return (
    <section className="mx-auto max-w-7xl px-4 pb-2 pt-6 md:px-6 md:pt-10">
      <div className="grid gap-5 lg:grid-cols-3">
        {/* 焦点大瓜 */}
        <Link
          to={`/news/${featured.slug}`}
          className="group relative block min-h-[420px] overflow-hidden rounded-3xl md:min-h-[520px] lg:col-span-2"
        >
          <img
            src={featured.hero}
            alt={featured.title}
            title={featured.title}
            className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" aria-hidden="true" />
          <div className="relative flex h-full min-h-[420px] flex-col justify-end p-6 md:min-h-[520px] md:p-9">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <CategoryBadge slug={featured.category} small />
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-2.5 py-0.5 text-[11px] font-bold text-background-50">
                <i className="ri-fire-fill" aria-hidden="true" /> 本期最大瓜
              </span>
            </div>
            <h1 className="font-heading text-2xl leading-tight text-background-50 md:text-4xl">
              {featured.title}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">{featured.summary}</p>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <MetaLine a={featured} light />
            </div>
            <span className="mt-6 inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors group-hover:bg-primary-600">
              展开吃瓜 <i className="ri-arrow-right-line" aria-hidden="true" />
            </span>
          </div>
        </Link>
        {/* 侧边精选 */}
        <div className="flex flex-col justify-between gap-4 rounded-3xl bg-background-100 p-4 md:p-5">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-accent-600">
              <span className="h-px w-6 bg-accent-400" aria-hidden="true" />
              抢先看
            </p>
            <h2 className="font-heading text-xl text-foreground-950">今日新瓜速递</h2>
          </div>
          <div className="flex flex-col gap-3">
            {side.map((a) => (
              <PostCardSpot key={a.slug} a={a} />
            ))}
            <div className="mt-1 rounded-2xl bg-background-50 p-4">
              <p className="text-sm font-semibold text-foreground-900">想第一时间吃到大瓜？</p>
              <p className="mt-1 text-xs leading-relaxed text-foreground-500">订阅栏目更新，塌房、恋情、红毯一手瓜料不错过。</p>
              <Link to="/about" className="mt-3 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-accent-600 hover:text-accent-700">
                了解更多 <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoryStrip() {
  return (
    <section className="mx-auto max-w-7xl px-4 pt-6 md:px-6 md:pt-8">
      <SectionHead kicker="按栏目吃瓜" title="挑你感兴趣的瓜" desc="五大栏目 · 分类追更，想看的瓜这里都分好了。" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {categories.map((c) => {
          const count = articles.filter((a) => a.category === c.slug).length;
          const t = tone[c.role];
          return (
            <Link
              key={c.slug}
              to={`/column/${c.slug}`}
              className="group relative block overflow-hidden rounded-2xl"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={c.cover}
                  alt={c.name}
                  title={`${c.name}栏目`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-transparent" aria-hidden="true" />
              </div>
              <div className="absolute inset-x-0 bottom-0 p-4">
                <span className={`mb-2 inline-flex h-9 w-9 items-center justify-center rounded-lg ${t.solid} shadow-sm`}>
                  <i className={c.icon} aria-hidden="true" />
                </span>
                <h3 className="font-heading text-lg text-background-50">{c.name}</h3>
                <p className="text-xs text-background-200">{c.tagline}</p>
                <p className="mt-1 text-[11px] font-medium text-accent-300">{count} 篇瓜报</p>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function HotRank() {
  const ranks = ['tafang-toushui', 'tafang-jiachang', 'lianqing-tongchuang', 'juzu-xuancai', 'zongyi-juben', 'hongtan-chuanfa']
    .map((s) => getArticle(s)!)
    .sort((x, y) => y.views - x.views);
  const redCarpet = byCategory('hongtan');
  return (
    <section className="mx-auto max-w-7xl px-4 pt-12 md:px-6 md:pt-16">
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-3xl bg-background-100 p-5 md:p-7">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-primary-500">
                <span className="h-px w-6 bg-primary-400" aria-hidden="true" />
                Trending
              </p>
              <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">本周热瓜 TOP 榜</h2>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
              <i className="ri-fire-fill" aria-hidden="true" /> 火热更新中
            </span>
          </div>
          <div>
            {ranks.slice(0, 6).map((a, i) => (
              <RankRow key={a.slug} a={a} rank={i + 1} />
            ))}
          </div>
        </div>
        {/* 红毯名场面 */}
        <Link
          to="/column/hongtan"
          className="group relative block overflow-hidden rounded-3xl"
        >
          <img
            src="https://readdy.ai/api/search-image?query=glamorous%20red%20carpet%20moment%20with%20bursts%20of%20photographer%20flashes%20and%20golden%20confetti%20against%20deep%20navy%20night%2C%20elegant%20evening%20gown%20silhouette%2C%20rich%20amber%20and%20crimson%20lighting%2C%20luxurious%20editorial%20fashion%20photography&width=800&height=1200&seq=side-hongtan&orientation=portrait"
            alt="红毯名场面"
            title="红毯名场面"
            className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/20 to-transparent" aria-hidden="true" />
          <div className="relative flex h-full min-h-[420px] flex-col justify-between p-6 md:min-h-[520px]">
            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-background-50">
              <i className="ri-camera-3-fill" aria-hidden="true" /> 红毯名场面
            </span>
            <div>
              <h2 className="font-heading text-2xl text-background-50 md:text-3xl">星光与翻车<br />都在这里</h2>
              <p className="mt-2 text-sm text-background-200">造型盘点 · 惊艳与惊险并存，今年红毯的精彩远不止造型。</p>
              <span className="mt-4 flex flex-col gap-2">
                {redCarpet.slice(0, 2).map((a) => (
                  <span key={a.slug} className="inline-flex items-center gap-2 text-sm text-background-100 transition-colors group-hover:text-accent-300">
                    <i className="ri-arrow-right-s-line text-accent-400" aria-hidden="true" /> {a.title}
                  </span>
                ))}
              </span>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}

function ShowSpotlight() {
  const zongyi = byCategory('zongyi');
  return (
    <section className="mx-auto max-w-7xl px-4 pt-12 md:px-6 md:pt-16">
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="relative min-h-[340px] overflow-hidden rounded-3xl lg:min-h-[460px]">
          <img
            src="https://readdy.ai/api/search-image?query=backstage%20of%20a%20variety%20show%20with%20spotlights%20and%20monitor%20walls%2C%20editor%20desk%20with%20timeline%20screens%2C%20subtle%20electric%20amber%20and%20magenta%20light%2C%20analytical%20mood%2C%20behind%20the%20scenes%20documentary%20photography&width=1200&height=720&seq=banner-zongyi&orientation=landscape"
            alt="综艺幕后剧本显微镜"
            title="综艺幕后栏目"
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/40 to-transparent" aria-hidden="true" />
          <div className="relative flex h-full flex-col justify-end p-6 md:p-8">
            <span className="mb-2 inline-flex w-fit items-center gap-1 rounded-full bg-secondary-500 px-3 py-1 text-xs font-bold text-background-50">
              <i className="ri-tv-2-line" aria-hidden="true" /> 综艺幕后
            </span>
            <h2 className="font-heading text-2xl text-background-50 md:text-3xl">节目剧本显微镜，拆穿那些"刚刚好"</h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-background-200">
              从赛制设计到剪辑逻辑，逐帧拆解综艺里那些"巧合"与"真实反应"背后的制作魔法。
            </p>
          </div>
        </div>
        <div className="flex flex-col justify-center rounded-3xl bg-background-100 p-6 md:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-secondary-600">本期深扒</p>
          <div className="mt-3 divide-y divide-background-200">
            {zongyi.map((a) => (
              <Link key={a.slug} to={`/news/${a.slug}`} className="group flex flex-col gap-1 py-4">
                <span className="line-clamp-2 text-base font-bold text-foreground-950 transition-colors group-hover:text-secondary-700 md:text-lg">
                  {a.title}
                </span>
                <span className="text-sm text-foreground-500">{a.date} · {formatViews(a.views)} 阅读</span>
              </Link>
            ))}
          </div>
          <Link to="/column/zongyi" className="mt-2 inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-full bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600">
            进入综艺幕后栏目 <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function TafangJuzu() {
  const groups: { slug: 'tafang' | 'juzu'; title: string; note: string }[] = [
    { slug: 'tafang', title: '塌房现场 · 人设崩塌实录', note: '明星光环破碎的每一个瞬间' },
    { slug: 'juzu', title: '剧组爆料 · 片场秘密档案', note: '摄影棚里不为人知的那一面' },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 pt-12 md:px-6 md:pt-16">
      <div className="grid gap-5 lg:grid-cols-2">
        {groups.map((g) => {
          const t = tone[g.slug === 'tafang' ? 'primary' : 'secondary'];
          return (
            <div key={g.slug} className="rounded-3xl bg-background-100 p-5 md:p-7">
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">{g.title}</h2>
                  <p className="mt-1 text-sm text-foreground-600">{g.note}</p>
                </div>
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${t.solid}`}>
                  <i className={getCategory(g.slug)?.icon ?? 'ri-fire-line'} aria-hidden="true" />
                </span>
              </div>
              <div className="flex flex-col gap-1">
                {byCategory(g.slug).map((a) => (
                  <PostCardSpot key={a.slug} a={a} />
                ))}
              </div>
              <Link
                to={`/column/${g.slug}`}
                className={`mt-2 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-foreground-900 hover:text-background-50`}
              >
                查看更多 {g.title.split('·')[0]}
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function LoveGrid() {
  const lianqing = byCategory('lianqing');
  return (
    <section className="mx-auto max-w-7xl px-4 pt-12 md:px-6 md:pt-16">
      <SectionHead
        kicker="恋情实锤"
        title="爱豆恋情追踪簿"
        desc="同框路透、时间线复盘，从蛛丝马迹到实锤画面，一步步揭开爱豆感情的真面目。"
        moreTo="/column/lianqing"
      />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {lianqing.map((a) => (
          <PostCardVertical key={a.slug} a={a} />
        ))}
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-500 via-primary-600 to-primary-800 px-6 py-10 text-center md:px-10 md:py-16">
        <div className="pointer-events-none absolute -left-6 -top-6 h-40 w-40 rounded-full bg-accent-400/30 blur-2xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-10 right-0 h-52 w-52 rounded-full bg-accent-500/20 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1 text-xs font-bold text-background-50">
            <i className="ri-chat-3-fill" aria-hidden="true" /> 吃瓜指南
          </span>
          <h2 className="mt-4 font-heading text-2xl text-background-50 md:text-4xl">关于吃瓜这件事，我们有自己的原则</h2>
          <p className="mt-3 text-sm leading-relaxed text-primary-100 md:text-base">
            理性吃瓜、拒绝造谣，是我们一直在坚持的事。来看看我们的内容准则与常见问题，也欢迎你投稿线索。
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/about" className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-accent-100">
              <i className="ri-question-line" aria-hidden="true" /> 了解吃瓜指南
            </Link>
            <a href="mailto:hello@meirichigua.com" className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-50/40 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10">
              <i className="ri-mail-send-line" aria-hidden="true" /> 我要投稿爆料
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  usePageMeta('每日吃瓜网 - 明星塌房现场 爱豆恋情实锤 娱乐圈八卦速递');
  return (
    <>
      <Ticker />
      <Hero />
      <CategoryStrip />
      <HotRank />
      <ShowSpotlight />
      <TafangJuzu />
      <LoveGrid />
      <CtaBand />
    </>
  );
}